'use client';
import cn from 'classnames';
import Image from 'next/image';
import { HeaderCMSContent } from '@/components/common/Header/Header';
import Text from '@/components/common/Text/Text';
import Link from '@/components/common/Link/Link';

import './MegaMenu.style.scss';

type MegaMenuProps = {
  className?: string;
  cmsContent?: HeaderCMSContent;
};

function MegaMenu(props: MegaMenuProps) {
  const { className = '', cmsContent } = props;
  const renderDesktopCard = (
    card: HeaderCMSContent['desktopHeaderCard'],
    modifier: 'primary' | 'secondary'
  ) => {
    if (!card) return null;
    const img = (
      <Image
        className={cn(
          'MegaMenu-headerCard',
          'MegaMenu-headerCard-desktop',
          `MegaMenu-headerCard-desktop-${modifier}`
        )}
        src={card.image}
        height={card.imageHeight}
        width={card.imageWidth}
        style={{ width: card.imageWidth, height: card.imageHeight }}
        alt={card.imageAltText}
      />
    );
    return card.url ? <a href={card.url}>{img}</a> : img;
  };

  const hasAnyDesktopCard =
    cmsContent?.desktopHeaderCard || cmsContent?.desktopHeaderCardSecondary;

  return (
    <div className={cn('MegaMenu', 'shadow-bottom', className)}>
      <div className='col col-collection'>
        <Text
          className='col-title'
          variant='title-xxs'
          uppercase
        >
          The collection
        </Text>
        {/* 
          Using regular links here because if the user is on the PLP, the query string changes,
          but the page itself won't update since it's higher in the component tree
          than the PLP page. 

          These links technically slow the page down and prevent pre-loading,
          but it will perform a full refresh of the page on click, which is OK.
          Functionality over framework
        */}
        <a
          className='col-item Link link-m'
          href='/collection'
        >
          VIEW ALL
        </a>
        <a
          className='col-item Link link-m'
          href='/collection?collection=Handbags'
        >
          HANDBAGS
        </a>
        <a
          className='col-item Link link-m'
          href='/collection?collection=Designer%20Jewelry'
        >
          DESIGNER JEWELRY
        </a>
        <a
          className='col-item Link link-m'
          href='/collection?collection=Fine%20Jewelry'
        >
          FINE JEWELRY
        </a>
      </div>
      <div className='col col-tiers'>
        <Text
          className='col-title'
          variant='title-xxs'
          uppercase
        >
          Tiers
        </Text>
        <a
          className='col-item Link link-m'
          href='/collection?subscriptionLevel=Couture'
        >
          COUTURE
        </a>
        <a
          className='col-item Link link-m'
          href='/collection?subscriptionLevel=Classique'
        >
          CLASSIQUE
        </a>
        <a
          className='col-item Link link-m'
          href='/collection?subscriptionLevel=Premier'
        >
          PREMIER
        </a>
        <a
          className='col-item Link link-m'
          href='/collection/reserve'
        >
          RÉSERVÉ
        </a>
      </div>

      {cmsContent?.featuredLinks && (
        <div className='col col-featured'>
          <Text
            className='col-title'
            variant='title-xxs'
            uppercase
          >
            Featured
          </Text>
          {cmsContent?.featuredLinks.map((link, index) => {
            return (
              <Link
                key={index}
                className={cn('col-item', {
                  'col-item-image': link.imageUrl
                })}
                href={link.url}
                variant='link-m'
                uppercase
                aria-label={link.displayText}
              >
                {link.imageUrl ? (
                  <Image
                    className='col-item-image-img'
                    src={link.imageUrl}
                    alt={link.displayText}
                    height={14}
                    width={100}
                  />
                ) : (
                  link.displayText
                )}
              </Link>
            );
          })}
        </div>
      )}
      {hasAnyDesktopCard && (
        <div className='col col-cards'>
          {renderDesktopCard(cmsContent?.desktopHeaderCard, 'primary')}
          {renderDesktopCard(
            cmsContent?.desktopHeaderCardSecondary,
            'secondary'
          )}
        </div>
      )}
    </div>
  );
}

export default MegaMenu;
